import React, { useState, useRef, useEffect, useCallback } from 'react';
import { 
  Table, 
  Button, 
  Input, 
  Space, 
  Tag, 
  message, 
  Dropdown, 
  Select, 
  Popconfirm, 
  Drawer, 
  Form, 
  Spin, 
  Tabs,
  Tooltip,
  Collapse,
  Typography,
  Divider,
  Modal
} from 'antd';
import { 
  PlusOutlined, 
  SearchOutlined, 
  MoreOutlined, 
  EditOutlined, 
  CopyOutlined, 
  CheckCircleOutlined, 
  CloseCircleOutlined, 
  DeleteOutlined,
  SaveOutlined,
  CodeOutlined,
  EyeOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Editor, { Monaco } from '@monaco-editor/react';
import dayjs from 'dayjs';

// API Services
import { snippetsApi, templatesApi, contractsApi, DocumentSnippet } from '../../services/api';

// Design System
import { CardContainer } from '../../components/contract/CardContainer';
import { SectionHeader } from '../../components/contract/SectionHeader';

const { Text } = Typography;
const { Panel } = Collapse;
const { TabPane } = Tabs;

// --- Constants & Types ---

const VARIABLE_GROUPS = [
  {
    key: 'company',
    label: 'Company',
    variables: [
      { name: 'company.name', description: 'Company name (Rimtrade International)' },
      { name: 'company.address', description: 'Full company address' },
      { name: 'company.phone', description: 'Company phone number' },
      { name: 'company.email', description: 'Company email' },
      { name: 'company.website', description: 'Company website' },
      { name: 'logoUrl', description: 'URL to company logo image' },
      { name: 'signatureUrl', description: 'URL to signature image' },
    ],
  },
  {
    key: 'contract',
    label: 'Contract',
    variables: [
      { name: 'contract.reference', description: 'Contract reference (e.g., CR-BN24001)' },
      { name: 'contract.contractDate', description: 'Contract date formatted' },
      { name: 'contract.status', description: 'Contract status' },
      { name: 'contract.cropYear', description: 'Crop year' },
      { name: 'contract.commodity', description: 'Main commodity' },
      { name: 'contract.hsCode', description: 'HS Code' },
      { name: 'contract.paymentTermsSeller', description: 'Payment terms for seller' },
      { name: 'contract.paymentTermsBuyer', description: 'Payment terms for buyer' },
      { name: 'contract.basisSeller', description: 'Price basis for seller' },
      { name: 'contract.basisBuyer', description: 'Price basis for buyer' },
      { name: 'contract.commissionPercentage', description: 'Commission percentage' },
      { name: 'contract.sellerNotes', description: 'Notes for seller contract' },
      { name: 'contract.buyerNotes', description: 'Notes for buyer contract' },
      { name: 'contract.documents', description: 'Document requirements' },
      { name: 'contract.revised', description: 'Whether contract is revised' },
      { name: 'contract.shipmentsLabel', description: 'Shipments label (e.g., .01, .02)' },
    ],
  },
  {
    key: 'supplier',
    label: 'Supplier',
    variables: [
      { name: 'supplier.name', description: 'Supplier full name' },
      { name: 'supplier.shortName', description: 'Supplier short name/code' },
      { name: 'supplier.address', description: 'Supplier address' },
      { name: 'supplier.country', description: 'Supplier country' },
      { name: 'supplier.code', description: 'Supplier code' },
    ],
  },
  {
    key: 'buyer',
    label: 'Buyer',
    variables: [
      { name: 'buyer.name', description: 'Buyer full name' },
      { name: 'buyer.shortName', description: 'Buyer short name/code' },
      { name: 'buyer.address', description: 'Buyer address' },
      { name: 'buyer.country', description: 'Buyer country' },
      { name: 'buyer.code', description: 'Buyer code' },
    ],
  },
  {
    key: 'shipment',
    label: 'Shipment (use in #each)',
    variables: [
      { name: 'shipments', description: 'Array of shipments - use {{#each shipments}}' },
      { name: 'shipment', description: 'Current/first shipment object' },
      { name: 'reference', description: 'Shipment reference (inside each)' },
      { name: 'shipmentNumber', description: 'Shipment number' },
      { name: 'startDate', description: 'Shipment start date' },
      { name: 'endDate', description: 'Shipment end date' },
      { name: 'portOfLoading', description: 'Port of loading' },
      { name: 'portOfDischarge', description: 'Port of discharge' },
      { name: 'destination', description: 'Destination' },
      { name: 'bookingNumber', description: 'Booking number' },
      { name: 'etd', description: 'Estimated time of departure' },
      { name: 'eta', description: 'Estimated time of arrival' },
      { name: 'packingNotes', description: 'Packing notes' },
      { name: 'basisSeller', description: 'Shipping basis (seller)' },
      { name: 'basisBuyer', description: 'Shipping basis (buyer)' },
      { name: 'totalLbs', description: 'Total pounds' },
      { name: 'totalUnits', description: 'Total units' },
    ],
  },
  {
    key: 'container',
    label: 'Container (use in #each)',
    variables: [
      { name: 'containers', description: 'Array of containers - use {{#each containers}}' },
      { name: 'containerNumber', description: 'Container number (inside each)' },
      { name: 'sealNumber', description: 'Seal number' },
      { name: 'equipmentType', description: 'Equipment type (40HC, etc.)' },
      { name: 'netWeight', description: 'Net weight' },
      { name: 'grossWeight', description: 'Gross weight' },
    ],
  },
  {
    key: 'goods',
    label: 'Goods (use in #each)',
    variables: [
      { name: 'goods', description: 'Array of goods - use {{#each goods}}' },
      { name: 'description', description: 'Goods description (inside each)' },
      { name: 'variety', description: 'Variety' },
      { name: 'grade', description: 'Grade' },
      { name: 'size', description: 'Size' },
      { name: 'numUnits', description: 'Number of units' },
      { name: 'lbsUnit', description: 'Pounds per unit' },
      { name: 'unit', description: 'Unit type (cases, bags, etc.)' },
      { name: 'commodity', description: 'Commodity/brand name' },
      { name: 'priceSeller', description: 'Price for seller' },
      { name: 'priceBuyer', description: 'Price for buyer' },
    ],
  },
  {
    key: 'summary',
    label: 'Summary & Totals',
    variables: [
      { name: 'hasMultipleShipments', description: 'Boolean: has multiple shipments' },
      { name: 'containerCount', description: 'Total container count' },
      { name: 'equipment', description: 'Equipment summary' },
      { name: 'goodsSummary', description: 'Summary of all goods' },
      { name: 'goodsDetail', description: 'Detailed goods breakdown' },
      { name: 'totals.packages', description: 'Total packages' },
      { name: 'totals.netWeight', description: 'Total net weight' },
      { name: 'totals.grossWeight', description: 'Total gross weight' },
      { name: 'today', description: 'Current date' },
      { name: 'generatedAt', description: 'Generation timestamp' },
    ],
  },
  {
    key: 'helpers',
    label: 'Handlebars Helpers',
    variables: [
      { name: 'formatCurrency value', description: '{{formatCurrency amount}} - $1,234.56' },
      { name: 'formatDate value', description: '{{formatDate date}} - 01/08/2026' },
      { name: 'formatDateLong value', description: '{{formatDateLong date}} - 8 January 2026' },
      { name: 'formatNumber value', description: '{{formatNumber num}} - 1,234.56' },
      { name: '#if condition', description: '{{#if value}}...{{/if}}' },
      { name: '#each array', description: '{{#each items}}...{{/each}}' },
      { name: 'eq a b', description: '{{#if (eq status "active")}}' },
      { name: 'default value fallback', description: '{{default value "N/A"}}' },
      { name: 'uppercase value', description: '{{uppercase text}}' },
    ],
  },
];

interface SnippetFormData {
  name: string;
  label: string;
  description: string;
  content: string;
  isActive: boolean;
}

// --- Main Component ---

const DocumentSnippetsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<boolean | undefined>();
  
  // Drawer & Editor State
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingSnippet, setEditingSnippet] = useState<DocumentSnippet | null>(null);
  const [form] = Form.useForm<SnippetFormData>();
  const [editorContent, setEditorContent] = useState('');
  const editorRef = useRef<any>(null);
  
  // Preview State
  const [activeTab, setActiveTab] = useState('editor');
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [selectedContractId, setSelectedContractId] = useState<string | null>(null);

  // --- Queries & Mutations ---

  const { data, isLoading } = useQuery({
    queryKey: ['snippets', statusFilter],
    queryFn: async () => {
      const params: Record<string, unknown> = { limit: 100 };
      if (statusFilter !== undefined) params.isActive = statusFilter;
      return snippetsApi.list(params);
    },
  });

  const { data: contractsData } = useQuery({
    queryKey: ['contracts-for-preview'],
    queryFn: () => contractsApi.getContracts({ limit: 50 }),
    enabled: drawerOpen,
  });

  const contracts = (contractsData as any)?.contracts || [];

  const createMutation = useMutation({
    mutationFn: snippetsApi.create,
    onSuccess: () => {
      message.success('Snippet created successfully');
      setDrawerOpen(false);
      queryClient.invalidateQueries({ queryKey: ['snippets'] });
    },
    onError: (error: any) => {
      message.error(error.response?.data?.error || 'Failed to create snippet');
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<SnippetFormData> }) => 
      snippetsApi.update(id, data),
    onSuccess: () => {
      message.success('Snippet updated successfully');
      setDrawerOpen(false);
      queryClient.invalidateQueries({ queryKey: ['snippets'] });
    },
    onError: (error: any) => {
      message.error(error.response?.data?.error || 'Failed to update snippet');
    }
  });

  const deleteMutation = useMutation({
    mutationFn: snippetsApi.delete,
    onSuccess: () => {
      message.success('Snippet deleted');
      queryClient.invalidateQueries({ queryKey: ['snippets'] });
    },
    onError: () => message.error('Failed to delete snippet'),
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) => 
      snippetsApi.update(id, { isActive }),
    onSuccess: () => {
      message.success('Status updated');
      queryClient.invalidateQueries({ queryKey: ['snippets'] });
    },
    onError: () => message.error('Failed to update status'),
  });

  // --- Handlers ---

  const handleOpenDrawer = (snippet?: DocumentSnippet) => {
    form.resetFields();
    setActiveTab('editor');
    setPreviewHtml('');
    
    if (snippet) {
      setEditingSnippet(snippet);
      setEditorContent(snippet.content);
      form.setFieldsValue({
        name: snippet.name,
        label: snippet.label,
        description: snippet.description || '',
        isActive: snippet.isActive,
        content: snippet.content
      });
    } else {
      setEditingSnippet(null);
      setEditorContent('');
      form.setFieldsValue({
        isActive: true,
        content: ''
      });
    }
    
    setDrawerOpen(true);
    
    // Auto-select first contract for preview if available
    if (contracts.length > 0 && !selectedContractId) {
      setSelectedContractId(contracts[0].id);
    }
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setEditingSnippet(null);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      // Ensure content comes from editor state, not just form
      const data = { ...values, content: editorContent };
      
      if (editingSnippet) {
        updateMutation.mutate({ id: editingSnippet.id, data });
      } else {
        createMutation.mutate(data);
      }
    } catch (error) {
      // Form validation failed
    }
  };

  const handleEditorMount = (editor: any) => {
    editorRef.current = editor;
  };

  const insertVariable = (variableName: string) => {
    if (!editorRef.current) return;
    
    const editor = editorRef.current;
    const selection = editor.getSelection();
    const isHelper = variableName.includes(' ');
    const text = isHelper ? `{{${variableName}}}` : `{{${variableName}}}`;

    editor.executeEdits('insert-variable', [
      {
        range: selection || { startLineNumber: 1, startColumn: 1, endLineNumber: 1, endColumn: 1 },
        text,
        forceMoveMarkers: true,
      },
    ]);
    editor.focus();
  };

  const updatePreview = useCallback(async () => {
    if (!editorContent || !selectedContractId) {
      setPreviewHtml('<p style="color: #999; text-align: center; padding: 20px;">Select a contract to preview</p>');
      return;
    }

    setPreviewLoading(true);
    try {
      const html = await templatesApi.preview({
        content: `<!DOCTYPE html><html><head><style>body{font-family:Arial,sans-serif;margin:10px;}</style></head><body>${editorContent}</body></html>`,
        contractId: selectedContractId,
      });
      setPreviewHtml(html as string);
    } catch (error: any) {
      setPreviewHtml(`<div style="color: red; padding: 10px;">Error: ${error.message}</div>`);
    } finally {
      setPreviewLoading(false);
    }
  }, [editorContent, selectedContractId]);

  // Update preview when tab changes to preview
  useEffect(() => {
    if (activeTab === 'preview' && drawerOpen) {
      updatePreview();
    }
  }, [activeTab, drawerOpen, updatePreview]);

  // --- Table Columns ---

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      render: (name: string) => <code style={{ color: '#1677ff' }}>{`{{> ${name}}}`}</code>,
    },
    {
      title: 'Label',
      dataIndex: 'label',
      key: 'label',
      render: (label: string, record: DocumentSnippet) => (
        <div>
          <div style={{ fontWeight: 500 }}>{label}</div>
          {record.description && (
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
              {record.description}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 100,
      render: (record: DocumentSnippet) => (
        record.isActive ? 
          <Tag icon={<CheckCircleOutlined />} color="success">Active</Tag> : 
          <Tag icon={<CloseCircleOutlined />} color="default">Inactive</Tag>
      ),
    },
    {
      title: 'Last Modified',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 150,
      render: (date: string) => dayjs(date).format('MMM D, YYYY'),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      render: (record: DocumentSnippet) => (
        <Dropdown
          menu={{
            items: [
              {
                key: 'edit',
                icon: <EditOutlined />,
                label: 'Edit',
                onClick: () => handleOpenDrawer(record),
              },
              { type: 'divider' },
              {
                key: 'toggle',
                label: record.isActive ? 'Deactivate' : 'Activate',
                onClick: () => toggleActiveMutation.mutate({ id: record.id, isActive: !record.isActive }),
              },
              {
                key: 'delete',
                icon: <DeleteOutlined />,
                label: 'Delete',
                danger: true,
                onClick: () => {
                  Modal.confirm({
                    title: 'Delete Snippet',
                    content: `Are you sure you want to delete "${record.label}"?`,
                    okText: 'Delete',
                    okType: 'danger',
                    onOk: () => deleteMutation.mutate(record.id)
                  });
                },
              },
            ],
          }}
          trigger={['click']}
        >
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];

  const snippets = data?.snippets || [];
  const filteredSnippets = snippets.filter(s => {
    const matchesSearch = !searchTerm || 
      s.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
      s.label.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  return (
    <div style={{ padding: '24px' }}>
      <CardContainer variant="primary">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div>
            <h1 className="text-h1">Document Snippets</h1>
            <Text type="secondary">Manage reusable content blocks for document generation</Text>
          </div>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => handleOpenDrawer()} size="large">
            New Snippet
          </Button>
        </div>

        <div style={{ display: 'flex', gap: '16px', marginBottom: '24px' }}>
          <Input
            placeholder="Search snippets..."
            prefix={<SearchOutlined />}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ maxWidth: 300 }}
            allowClear
          />
          <Select
            placeholder="Status"
            value={statusFilter}
            onChange={setStatusFilter}
            allowClear
            style={{ width: 120 }}
            options={[
              { value: true, label: 'Active' },
              { value: false, label: 'Inactive' },
            ]}
          />
        </div>

        <Table
          columns={columns}
          dataSource={filteredSnippets}
          loading={isLoading}
          rowKey="id"
          pagination={{ pageSize: 10 }}
        />
      </CardContainer>

      {/* Edit/Create Drawer */}
      <Drawer
        title={editingSnippet ? 'Edit Snippet' : 'New Snippet'}
        width={900}
        open={drawerOpen}
        onClose={handleCloseDrawer}
        styles={{ body: { paddingBottom: 80 } }}
        extra={
          <Space>
            <Button onClick={handleCloseDrawer}>Cancel</Button>
            <Button 
              type="primary" 
              onClick={handleSave} 
              loading={createMutation.isPending || updateMutation.isPending}
            >
              Save
            </Button>
          </Space>
        }
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ isActive: true }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <Form.Item
              name="name"
              label="Snippet Name (Unique ID)"
              rules={[
                { required: true, message: 'Please enter a name' },
                { pattern: /^[a-zA-Z][a-zA-Z0-9_]*$/, message: 'Must start with letter, only letters/numbers/underscores' }
              ]}
              tooltip="Used in templates as {{> snippetName}}"
            >
              <Input placeholder="e.g. shipping_terms" disabled={!!editingSnippet} />
            </Form.Item>
            
            <Form.Item
              name="label"
              label="Display Label"
              rules={[{ required: true, message: 'Please enter a label' }]}
            >
              <Input placeholder="e.g. Standard Shipping Terms" />
            </Form.Item>
          </div>

          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} placeholder="Optional description of what this snippet contains" />
          </Form.Item>
          
          <Form.Item name="isActive" valuePropName="checked">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Input type="checkbox" style={{ width: 'auto' }} /> 
              <span>Active (available for use)</span>
            </div>
          </Form.Item>
          
          <Divider style={{ margin: '12px 0' }} />
          
          <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Tabs 
              activeKey={activeTab} 
              onChange={setActiveTab} 
              type="card"
              style={{ marginBottom: 0 }}
              items={[
                { label: <span><CodeOutlined /> Editor</span>, key: 'editor' },
                { label: <span><EyeOutlined /> Preview</span>, key: 'preview' }
              ]}
            />
            
            {activeTab === 'preview' && (
               <Select
                placeholder="Select contract for data"
                value={selectedContractId}
                onChange={setSelectedContractId}
                style={{ width: 250 }}
                size="small"
                options={contracts.map((c: any) => ({
                  value: c.id,
                  label: c.reference
                }))}
              />
            )}
          </div>

          <div style={{ height: '500px', display: 'flex', border: '1px solid #d9d9d9', borderRadius: '8px' }}>
             {/* Left side: Editor or Preview */}
             <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
               {activeTab === 'editor' ? (
                 <Editor
                   height="100%"
                   defaultLanguage="handlebars"
                   value={editorContent}
                   onChange={(value) => setEditorContent(value || '')}
                   onMount={handleEditorMount}
                   options={{
                     minimap: { enabled: false },
                     lineNumbers: 'on',
                     scrollBeyondLastLine: false,
                     wordWrap: 'on',
                     automaticLayout: true,
                   }}
                 />
               ) : (
                 <div style={{ flex: 1, padding: '20px', overflow: 'auto', backgroundColor: '#fff' }}>
                    {previewLoading ? (
                      <div style={{ display: 'flex', justifyContent: 'center', marginTop: '50px' }}>
                        <Spin tip="Generating preview..." />
                      </div>
                    ) : (
                      <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
                    )}
                 </div>
               )}
             </div>

             {/* Right side: Variable Picker (only in editor mode) */}
             {activeTab === 'editor' && (
               <div style={{ width: '250px', borderLeft: '1px solid #d9d9d9', display: 'flex', flexDirection: 'column', backgroundColor: '#fafafa' }}>
                 <div style={{ padding: '8px 12px', borderBottom: '1px solid #d9d9d9', fontWeight: 600, fontSize: '12px' }}>
                   Variables <Tooltip title="Click to insert"><InfoCircleOutlined /></Tooltip>
                 </div>
                 <div style={{ flex: 1, overflowY: 'auto', padding: '0 4px' }}>
                   <Collapse 
                     ghost 
                     size="small" 
                     defaultActiveKey={['company']}
                     expandIconPosition="end"
                   >
                     {VARIABLE_GROUPS.map(group => (
                       <Panel header={<span style={{ fontSize: '12px' }}>{group.label}</span>} key={group.key}>
                         {group.variables.map(v => (
                           <div 
                             key={v.name}
                             onClick={() => insertVariable(v.name)}
                             style={{ 
                               cursor: 'pointer', 
                               padding: '4px 8px', 
                               borderRadius: '4px',
                               marginBottom: '2px',
                               fontSize: '11px',
                               transition: 'all 0.2s'
                             }}
                             className="variable-item"
                             onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e6f7ff'}
                             onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                           >
                             <div style={{ color: '#1677ff', fontFamily: 'monospace' }}>{`{{${v.name}}}`}</div>
                             <div style={{ color: '#888', fontSize: '10px' }}>{v.description}</div>
                           </div>
                         ))}
                       </Panel>
                     ))}
                   </Collapse>
                 </div>
               </div>
             )}
          </div>
        </Form>
      </Drawer>
    </div>
  );
};

export default DocumentSnippetsPage;
